import { WebStorageStateStore, type UserManagerSettings } from "oidc-client-ts";

/**
 * Replaces next-auth@5.0.0-beta.31 entirely. The original NextAuth setup
 * (src/auth.ts) had two structural problems worth not carrying forward:
 * (1) it sent an Authorization header on only 1 of 55 backend calls — see
 * api/baseQuery.ts, which fixes that centrally; (2) `middleware.ts`
 * imported the full provider config instead of an edge-safe split, which
 * doesn't even apply here since there is no middleware layer on static
 * Pages hosting.
 *
 * This also unifies the web app's auth flow with the mobile app's, which
 * already does OIDC Authorization Code + PKCE against the same Asgardeo
 * tenant via expo-auth-session (see doh_mobile_frontend/app/services/auth.ts).
 */
const ASGARDEO_ISSUER = import.meta.env.VITE_ASGARDEO_ISSUER as string;
const ASGARDEO_CLIENT_ID = import.meta.env.VITE_ASGARDEO_CLIENT_ID as string;
const ASGARDEO_CLIENT_SECRET = import.meta.env.VITE_ASGARDEO_CLIENT_SECRET as string;
// Must string-match EXACTLY what's registered in Asgardeo's "Allowed
// logout URLs" for this app — a mismatch (e.g. a trailing slash) makes
// Asgardeo silently 302 to its own error page instead of completing
// end_session, leaving the IdP session alive. Configurable via env instead
// of hardcoded so it can be pinned to whatever's actually registered
// without a code change + redeploy cycle.
const POST_LOGOUT_REDIRECT_URI =
  (import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI as string | undefined) || `${window.location.origin}/`;

export const oidcConfig: UserManagerSettings = {
  authority: ASGARDEO_ISSUER,
  client_id: ASGARDEO_CLIENT_ID,
  client_secret: ASGARDEO_CLIENT_SECRET,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
  response_type: "code",
  scope: "openid email profile roles",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  loadUserInfo: true,
  // Deliberately NOT setting revokeTokensOnSignout: oidc-client-ts's
  // _signoutStart rethrows if the revoke call fails, which aborts the
  // whole signoutRedirect before the user ever reaches end_session.
  // Token revocation instead goes through the backend (see
  // AuthProvider.tsx's logout, which calls POST /api/auth/logout first)
  // so a revoke failure can never block the client-side signout.
  metadataUrl: `${ASGARDEO_ISSUER}/oauth2/token/.well-known/openid-configuration`,
  metadata: {
    issuer: `${ASGARDEO_ISSUER}/oauth2/token`,
    authorization_endpoint: `${ASGARDEO_ISSUER}/oauth2/authorize`,
    token_endpoint: `${ASGARDEO_ISSUER}/oauth2/token`,
    userinfo_endpoint: `${ASGARDEO_ISSUER}/oauth2/userinfo`,
    jwks_uri: `${ASGARDEO_ISSUER}/oauth2/jwks`,
    end_session_endpoint: `${ASGARDEO_ISSUER}/oidc/logout`,
    revocation_endpoint: `${ASGARDEO_ISSUER}/oauth2/revoke`,
    token_endpoint_auth_methods_supported: ["client_secret_post", "client_secret_basic"],
  },
};

export interface DecodedIdToken {
  sub?: string;
  roles?: string[];
  groups?: string[];
  given_name?: string;
  family_name?: string;
  email?: string;
  [key: string]: unknown;
}

export type AppRole = "DONOR" | "BLOOD_BANK" | "HOSPITAL" | "IT_SUPPORT" | "CAMP_ORGANIZER";

/**
 * This tenant's real top-level Asgardeo role-group names — verified
 * against live captured tokens — are "Blood Bank", "Hospital",
 * "IT Support", matching doh_web_frontend's own role-router
 * (src/app/api/auth/callback/page.tsx), not the "Internal/Staff"-style
 * names this used to check for (which don't exist in this tenant and
 * made every staff login resolve to DONOR).
 *
 * Asgardeo is NOT consistent about prefixing these with "Internal/" in
 * the ID token — two logins for the very same account produced
 * `roles: ["Blood Bank", "everyone"]` in one session and
 * `roles: ["Internal/Blood Bank", "Internal/everyone"]` in another.
 * (The backend's equivalent check, middleware/auth.ts on
 * cloudflare_doh_backend, already had to handle this for its own token
 * source — userinfo — and always saw the prefixed form there.) Stripping
 * a leading "Internal/" before matching handles both.
 *
 * Campaign Organizer is dynamically granted post-signup (see the
 * backend's assignCampaignOrganizer) rather than one of the tenant's
 * built-in groups, so it keeps the "Internal/CampaignOrg" name that
 * grant uses — already unprefixed-safe since we strip first.
 */
function stripInternalPrefix(claim: string): string {
  return claim.startsWith("Internal/") ? claim.slice("Internal/".length) : claim;
}

export function resolveRole(roles: string[] | undefined): AppRole {
  const list = (roles ?? []).map(stripInternalPrefix);
  if (list.includes("Blood Bank")) return "BLOOD_BANK";
  if (list.includes("Hospital")) return "HOSPITAL";
  if (list.includes("IT Support")) return "IT_SUPPORT";
  if (list.includes("CampaignOrg")) return "CAMP_ORGANIZER";
  return "DONOR";
}
